import React, { useState, useEffect } from "react";
import styles from "./adminUsersSearchResults.module.css";
import axios from "axios";
import { FaUserCheck } from "react-icons/fa";
import { FaUserLock } from "react-icons/fa";
import { ImBlocked } from "react-icons/im";
import { MdOutlineEmail } from "react-icons/md";
import { MdError } from "react-icons/md";
import BlockConfirmationModal from "./blockConfirmationModal/BlockConfirmationModal";
import UnblockConfirmationModal from "./unblockConfirmationModal/UnblockConfirmationModal";
import NewMessageModal from "../newMessageModal/NewMessageModal";

const apiUrl = process.env.REACT_APP_URL;

const AdminUsersSearchResults = ({
  currUsername,
  users,
  maxChars,
  maxSeconds,
}) => {
  /* This component is responsible for displaying a list of users received after an admin search for a username. Props: currUsername is the username of the admin who performed the search. users is an array of objects, each object represents a user. maxChars is the maximum number of characters in a text message. maxSeconds is the maximum length in seconds of a voice message. The last two props are intended for the case the admin wants to send a text / voice message to a user, or a text message to all users. This component allows the admin to send a message to a user, block / unblock a user, and reset a user's password. */

  const [usersState, setUsersState] = useState(users);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [showUnblockModal, setShowUnblockModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUserCurrStatus, setSelectedUserCurrStatus] = useState(null);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [errorSendingEmail, setErrorSendingEmail] = useState(false);
  const [currResetPasswordEmail, setCurrResetPasswordEmail] = useState(null);

  useEffect(() => {
    setUsersState(users);
  }, [users]);

  /**
   * Generate a random file name (used for audio messages).
   * @param {*} extension - the requested file extension to be used in the file name.
   * @returns a file name: random string, followed by dot and the passed extension.
   */
  const generateRandomFilename = (extension) => {
    const randomString = Math.random().toString(36).substring(2, 15);
    return `${randomString}.${extension}`;
  };

  /**
   * Handle message button click - show the modal that allows the admin to send a text / voice message to the user
   * @param {*} username - the username of the user to which the message is to be sent.
   */
  const handleMessageBtnClick = (username) => {
    setSelectedUser(username);
    setShowMessageModal(true);
  };

  /**
   * Close the message modal
   */
  const handleCloseMessageModal = () => {
    setShowMessageModal(false);
    setSelectedUser(null); // Reset the selected user when closing the modal
  };

  /**
   * Send a message to a user
   * @param {*} messageData - an object with type ("text" / "audio"), content and receiverUsername properties.
   */
  const handleSendMessage = async (messageData) => {
    const { type, content, receiverUsername } = messageData;

    if (!content || !receiverUsername) return;

    if (type === "text") {
      try {
        const response = await axios.post(
          `${apiUrl}/messages/new/text`,
          {
            senderUsername: currUsername,
            receiverUsername,
            textContent: content,
          },
          {
            withCredentials: true,
          }
        );

        if (response.status !== 201 || !response.data.success) {
          console.error("Failed to send text message:", response.data.message);
        }
      } catch (error) {
        console.error("Error sending text message:", error);
      }
    } else if (type === "audio") {
      try {
        const formData = new FormData();
        const randomFilename = generateRandomFilename("webm");
        formData.append("audio", content, randomFilename);
        formData.append("senderUsername", currUsername);
        formData.append("receiverUsername", receiverUsername);

        const response = await axios.post(
          `${apiUrl}/messages/new/voice`,
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
            withCredentials: true,
          }
        );

        if (response.status !== 201 || !response.data.success) {
          console.error("Failed to send voice message:", response.data.message);
        }
      } catch (error) {
        console.error("Error sending voice message:", error);
      }
    }
  };

  /**
   * Handle block button click - show the block user confirmation modal
   * @param {*} username - the username to block
   * @param {*} currStatus - the current status of the user ("active", "inactive", "blocked", "inactive-blocked", "closed")
   */
  const handleBlockBtnClick = (username, currStatus) => {
    setSelectedUser(username);
    setSelectedUserCurrStatus(currStatus);
    setShowBlockModal(true);
  };

  /**
   * Handle block confirmation by the admin - block the selected user.
   */
  const handleConfirmBlock = async () => {
    if (!selectedUser) return;

    // Update the user's status in the state
    setUsersState((prevUsers) =>
      prevUsers.map((user) => {
        let newStatus = "blocked";

        if (user.username === selectedUser && user.status === "inactive") {
          newStatus = "inactiveBlocked";
        }

        return user.username === selectedUser
          ? { ...user, status: newStatus }
          : user;
      })
    );

    // Block user
    try {
      let newStatus = "blocked";

      if (
        selectedUserCurrStatus === "inactive" ||
        selectedUserCurrStatus === "inactiveBlocked"
      ) {
        newStatus = "inactiveBlocked";
      }

      const response = await axios.put(
        `${apiUrl}/admin/users/block/${selectedUser}/${newStatus}`,
        {},
        { withCredentials: true }
      );

      if (response.status !== 200 || !response.data.success) {
        console.error(
          "DB Error - could not block the user. Please try again later."
        );
      }
    } catch (e) {
      console.error(
        "DB Error - could not block the user. Please try again later.",
        e
      );
    }

    setShowBlockModal(false);
    setSelectedUser(null);
  };

  /**
   * Handle unblock button click - show the unblock user confirmation modal
   * @param {*} username - the username of the user to unblock
   */
  const handleUnblockBtnClick = (username) => {
    setSelectedUser(username);
    setShowUnblockModal(true);
  };

  const handleConfirmUnblock = async () => {
    if (!selectedUser) return;

    // Update the user's status in the state
    setUsersState((prevUsers) =>
      prevUsers.map((user) => {
        let newStatus = "active";

        if (
          user.username === selectedUser &&
          user.status === "inactiveBlocked"
        ) {
          newStatus = "inactive";
        }

        return user.username === selectedUser
          ? { ...user, status: newStatus }
          : user;
      })
    );

    // Unblock user
    try {
      let newStatus = "active";

      if (
        selectedUserCurrStatus === "inactiveBlocked" ||
        selectedUserCurrStatus === "inactive"
      ) {
        newStatus = "inactive";
      }

      const response = await axios.put(
        `${apiUrl}/admin/users/block/${selectedUser}/${newStatus}`,
        {},
        { withCredentials: true }
      );

      if (response.status !== 200 || !response.data.success) {
        console.error(
          "DB Error - could not unblock the user. Please try again later."
        );
      }
    } catch (e) {
      console.error(
        "DB Error - could not unblock the user. Please try again later.",
        e
      );
    }

    setShowUnblockModal(false);
    setSelectedUser(null);
  };

  /**
   * Close the block / unblock modal
   */
  const handleCloseModal = () => {
    setShowBlockModal(false);
    setShowUnblockModal(false);
    setSelectedUser(null);
  };

  /**
   * Send a reset password email to a user.
   * @param {*} userEmail - the email address of the user to which the email should be sent.
   */
  const handleResetPassword = async (userEmail) => {
    try {
      await axios.post(`${apiUrl}/auth/send_recovery_email`, {
        email: userEmail,
      });

      setEmailSent(true);
      setErrorSendingEmail(false);
      setCurrResetPasswordEmail(userEmail);
    } catch (error) {
      console.error("Error sending email", error);
      setEmailSent(false);
      setErrorSendingEmail(true);
      setCurrResetPasswordEmail(userEmail);
    }
  };

  const usersList = usersState.map((user, idx) => (
    <div key={idx} className={styles.singleUser}>
      <div className={styles.userInfo}>
        <a
          href={`/profile/${user.username}`}
          target="_blank"
          rel="noopener noreferrer nofollow"
          style={{ textDecoration: "none" }}
        >
          <figure>
            <img src={user.img_link} alt={user.username} />
            <figcaption>{user.username}</figcaption>
          </figure>
        </a>

        <p>{user.email}</p>
        {currResetPasswordEmail === user.email && emailSent && (
          <p className={styles.sendEmailSuccess}>
            <MdOutlineEmail />
            <span>Password reset email sent</span>
          </p>
        )}

        {currResetPasswordEmail === user.email && errorSendingEmail && (
          <p className={styles.sendEmailError}>
            <MdError />
            <span>Error while sending password reset email</span>
          </p>
        )}
        <p
          className={
            user.status === "active"
              ? styles.activeUser
              : user.status === "blocked" || user.status === "inactiveBlocked"
              ? styles.blockedUser
              : styles.inactiveUser
          }
        >
          <span className={styles.userIcon}>
            {user.status === "active" ? (
              <FaUserCheck />
            ) : user.status === "blocked" ||
              user.status === "inactiveBlocked" ? (
              <ImBlocked />
            ) : (
              <FaUserLock />
            )}
          </span>
          {user.status === "active"
            ? "Active User"
            : user.status === "blocked"
            ? "Blocked User"
            : user.status === "inactiveBlocked"
            ? "Blocked Inactive user"
            : "Inactive User"}
        </p>
      </div>
      <div className={styles.userBtns}>
        {user.status !== "blocked" && user.status !== "inactiveBlocked" && (
          <button onClick={() => handleMessageBtnClick(user.username)}>
            Send Message
          </button>
        )}
        {user.status !== "blocked" && user.status !== "inactiveBlocked" && (
          <button onClick={() => handleResetPassword(user.email)}>
            Reset Password
          </button>
        )}
        {user.status !== "blocked" && user.status !== "inactiveBlocked" && (
          <button
            className={styles.blockBtn}
            onClick={() => handleBlockBtnClick(user.username, user.status)}
          >
            Block User
          </button>
        )}
        {(user.status === "blocked" || user.status === "inactiveBlocked") && (
          <button
            className={styles.unblockBtn}
            onClick={() => handleUnblockBtnClick(user.username, user.status)}
          >
            Unblock User
          </button>
        )}
      </div>
    </div>
  ));

  return (
    <div className={styles.adminUsersResults}>
      <p>The following users have been found:</p>
      {usersList}
      <BlockConfirmationModal
        show={showBlockModal}
        onClose={handleCloseModal}
        onConfirm={handleConfirmBlock}
      />
      <UnblockConfirmationModal
        show={showUnblockModal}
        onClose={handleCloseModal}
        onConfirm={handleConfirmUnblock}
      />
      {showMessageModal && (
        <NewMessageModal
          show={showMessageModal}
          onClose={handleCloseMessageModal}
          onSendMessage={handleSendMessage}
          receiverUsername={selectedUser}
          maxChars={maxChars}
          maxSeconds={maxSeconds}
        />
      )}
    </div>
  );
};

export default AdminUsersSearchResults;
